import { USER_STATUS_LABEL } from "@/modules/users";
import { hubTableCartao } from "@/shared/components/hub-table-cartao";
import type { User } from "@/modules/users";
import {
  EMPTY_CELL,
  NO_ROLE_LABEL,
  USER_INFO_TITLE,
  USERS_ACTIONS_KEY,
  USERS_COLUMNS,
  USERS_PHOTO_KEY,
} from "./users.constants";
import type { UserContactCardProps } from "@/shared/components/user-contact-card";
import type { UltimaAtividade, UserLinha, UsersColuna } from "./types-users";

const DATE_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
});
const TIME_FORMATTER = new Intl.DateTimeFormat("pt-BR", { timeStyle: "short" });

/** Data e hora em partes, como as duas linhas da célula no Figma (4458:2834, ano com 2 dígitos); valor ilegível sai inteiro na linha da data. */
function formatLastActivity(value: string): UltimaAtividade {
  if (!value) return { data: EMPTY_CELL, hora: "" };

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { data: value, hora: "" };

  return { data: DATE_FORMATTER.format(date), hora: TIME_FORMATTER.format(date) };
}

/** A linha da tabela com os textos e os rótulos das ações já prontos; a View só os desenha. */
export function buildUserLinha(user: User): UserLinha {
  const ativo = user.status === "ativo";

  return {
    id: user.id,
    user,
    nome: user.nome,
    email: user.email || EMPTY_CELL,
    time: user.time || EMPTY_CELL,
    perfil: user.papel || NO_ROLE_LABEL,
    semPerfil: !user.papel,
    ultimaAtividade: formatLastActivity(user.ultimaAtividade),
    status: USER_STATUS_LABEL[user.status],
    editLabel: `Editar usuário ${user.nome}`,
    editTestId: `users-edit-${user.id}`,
    toggleLabel: `${ativo ? "Inativar" : "Ativar"} usuário ${user.nome}`,
    // AC4: o texto acompanha a situação da linha.
    toggleTooltip: ativo ? "Desativar" : "Ativar",
    toggleTestId: `users-toggle-status-${user.id}`,
    statusTestId: `users-status-${user.id}`,
    noRoleTestId: `users-no-role-${user.id}`,
  };
}

/** Bloco "Informações do Usuário" dos modais: identidade só para consulta, com a foto de 64 do Figma. */
export function buildUserContato(user: User, testIdPrefix: string): UserContactCardProps {
  return {
    titulo: USER_INFO_TITLE,
    nome: user.nome,
    email: user.email,
    telefone: user.celular,
    detalhe: user.time,
    fotoUrl: user.fotoUrl,
    avatarSize: "lg",
    testIds: {
      root: `${testIdPrefix}-identity`,
      name: `${testIdPrefix}-name`,
      email: `${testIdPrefix}-email`,
      copy: `${testIdPrefix}-copy-email`,
      phone: `${testIdPrefix}-phone`,
      detail: `${testIdPrefix}-team`,
    },
  };
}

/** Colunas do Figma 4434:111149; Foto e Ações não ordenam e a coluna de Ações é centrada com a célula do cartão. */
export function buildUsersColunas(totalSemPerfil: number | null): UsersColuna[] {
  return USERS_COLUMNS.map(({ key, header, widthRem }) => {
    const acao = key === USERS_ACTIONS_KEY;

    return {
      key,
      header,
      width: `${widthRem}rem`,
      sortable: key !== USERS_PHOTO_KEY && !acao,
      align: acao ? "center" : undefined,
      className: acao ? hubTableCartao.actionCellClassName : undefined,
      selo: key === "papel" && totalSemPerfil ? buildSeloSemPerfil(totalSemPerfil) : undefined,
    };
  });
}

function buildSeloSemPerfil(total: number): NonNullable<UsersColuna["selo"]> {
  return {
    texto: String(total),
    titulo: `${total} ${total === 1 ? "usuário sem perfil" : "usuários sem perfil"}`,
    testId: "users-no-role-count",
  };
}
